import { useId, useState, type ComponentPropsWithRef, type ReactNode } from 'react';
import { Field, fieldDescribedBy } from '@src/components/Field/Field';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { CloseIcon } from '@src/components/icons/CloseIcon';
import { EyeIcon } from '@src/components/icons/EyeIcon';
import { EyeOffIcon } from '@src/components/icons/EyeOffIcon';
import { Typography, typeClassName } from '@src/components/Typography/Typography';
import './Input.css';

/** `password` is a prop rather than a type, so the reveal button cannot be lost. */
export type InputType = 'text' | 'email' | 'tel' | 'url' | 'search' | 'number';

type NativeInputProps = Omit<
  ComponentPropsWithRef<'input'>,
  'style' | 'value' | 'onChange' | 'type' | 'pattern' | 'prefix' | 'size'
>;

export interface InputProps extends NativeInputProps {
  /** Controlled, always a string — an empty field is `''`, never undefined. */
  value: string;
  /**
   * Value-first, like the v1 component: callers want the text, not the event.
   * Not called at all when `numeric` or `pattern` rejects the keystroke, so the
   * value in state can never be one the field would refuse.
   */
  onValueChange: (value: string) => void;
  label?: string;
  /** Sits opposite the label, and turns the field red. */
  error?: string;
  /** Opposite the label when there is no error — “Forgot password?”. */
  labelEnd?: ReactNode;
  /** Quiet note under the field. Shows alongside an error, not instead of it. */
  hint?: string;
  type?: InputType;
  /** Digits only. Also switches the on-screen keyboard to a numeric pad. */
  numeric?: boolean;
  /** Rejects anything that does not match. Must not carry the `g` flag — a
   * global regex is stateful and would accept every other keystroke. */
  pattern?: RegExp;
  /** Renders the field as a password with a reveal toggle. */
  password?: boolean;
  /** Shows a clear button while the field has a value. */
  onClear?: () => void;
  /** Static text inside the field, before the value — a currency, an @, a domain. */
  prefix?: string;
  /** Static text inside the field, after the value — a currency symbol. */
  suffix?: string;
  /** Glyph inside the field, before the value — the magnifier on a search box. */
  iconStart?: ReactNode;
  /** Enter without submitting a form. */
  onEnter?: () => void;
  /**
   * Keeps the label and hint rows occupied even when empty, so fields sitting
   * side by side line up whether or not each one has a label.
   */
  reserveSpace?: boolean;
  /** Takes the space left over on a shared row — a search box beside a button. */
  grow?: boolean;
  /** Sits on the input row, centred on the box. */
  action?: ReactNode;
  /** Accessible names for the two in-field buttons; pass translated copy. */
  revealLabel?: string;
  hideLabel?: string;
  clearLabel?: string;
  /** Parent id. The control, clear and reveal buttons hang off it. */
  testId?: string;
}

function isAccepted(value: string, numeric?: boolean, pattern?: RegExp): boolean {
  if (value === '') {
    return true;
  }
  if (numeric && !/^\d*$/.test(value)) {
    return false;
  }
  return !pattern || pattern.test(value);
}

export function Input({
  value,
  onValueChange,
  label,
  error,
  labelEnd,
  hint,
  type = 'text',
  numeric = false,
  pattern,
  password = false,
  onClear,
  prefix,
  suffix,
  iconStart,
  onEnter,
  reserveSpace = false,
  grow = false,
  action,
  revealLabel = 'Show',
  hideLabel = 'Hide',
  clearLabel = 'Clear',
  testId,
  className,
  disabled,
  id,
  onKeyDown,
  ...rest
}: InputProps) {
  const [revealed, setRevealed] = useState(false);

  const generatedId = useId();
  const inputId = id ?? generatedId;

  const showClear = Boolean(onClear) && value !== '' && !disabled;

  return (
    <Field
      controlId={inputId}
      label={label}
      error={error}
      labelEnd={labelEnd}
      hint={hint}
      disabled={disabled}
      reserveSpace={reserveSpace}
      grow={grow}
      action={action}
      testId={testId}
      className={className}
    >
      {iconStart && (
        <span className="input__icon" aria-hidden="true">
          {iconStart}
        </span>
      )}

      {prefix && (
        <Typography
          as="span"
          role="label/input"
          className="input__prefix"
          {...testIdAttr(testIdFor(testId, 'prefix'))}
        >
          {prefix}
        </Typography>
      )}

      <input
        className={`input__control ${typeClassName('label/input')}`}
        id={inputId}
        {...testIdAttr(testIdFor(testId, 'control'))}
        type={password && !revealed ? 'password' : type}
        value={value}
        disabled={disabled}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={fieldDescribedBy(inputId, { error, hint })}
        inputMode={numeric ? 'numeric' : undefined}
        onChange={(event) => {
          const next = event.target.value;
          if (isAccepted(next, numeric, pattern)) {
            onValueChange(next);
          }
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (event.key === 'Enter' && onEnter) {
            event.preventDefault();
            onEnter();
          }
        }}
        {...rest}
      />

      {suffix && (
        <Typography
          as="span"
          role="label/input"
          className="input__suffix"
          {...testIdAttr(testIdFor(testId, 'suffix'))}
        >
          {suffix}
        </Typography>
      )}

      {showClear && (
        <button
          className="input__action"
          type="button"
          aria-label={clearLabel}
          onClick={onClear}
          {...testIdAttr(testIdFor(testId, 'clear'))}
        >
          <CloseIcon />
        </button>
      )}

      {password && (
        <button
          className="input__action"
          type="button"
          aria-label={revealed ? hideLabel : revealLabel}
          aria-pressed={revealed}
          disabled={disabled}
          onClick={() => setRevealed((shown) => !shown)}
          {...testIdAttr(testIdFor(testId, 'reveal'))}
        >
          {revealed ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      )}
    </Field>
  );
}
